import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink } from "lucide-react";
import RichTextEditor from "../../components/RichTextEditor";
import PhotoPicker from "../../components/PhotoPicker";
import ImagePositioner from "../../components/ImagePositioner";

export default function AdminGuideEdit() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "",
    meta_description: "",
    hero_image: "",
    photographer_name: "",
    hero_position: "",
    content: "",
    published: true,
  });

  useEffect(() => {
    if (!user) return;
    api.get(`/guides/${slug}`)
      .then((r) => {
        setGuide(r.data);
        setForm({
          title: r.data.title || "",
          meta_description: r.data.meta_description || "",
          hero_image: r.data.hero_image || "",
          photographer_name: r.data.photographer_name || "",
          hero_position: r.data.hero_position || "",
          content: r.data.content || "",
          published: r.data.published ?? true,
        });
      })
      .catch((err) => {
        if (err.response?.status === 401) navigate("/admin/login");
        else if (err.response?.status === 404) navigate("/admin/guides");
      })
      .finally(() => setLoading(false));
  }, [slug, user, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/guides/${slug}`, form);
      toast.success("// GUIDE SAVED");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-24 text-center">
        <p className="font-mono text-[#CC1A1A] text-xs tracking-widest uppercase animate-pulse">// LOADING…</p>
      </div>
    );
  }

  if (!guide) return null;

  const label = (text) => (
    <label className="block font-mono text-[10px] tracking-[0.24em] uppercase text-[#9A8F83] mb-2">{text}</label>
  );

  const input = (key, placeholder) => (
    <input
      type="text"
      value={form[key]}
      onChange={(e) => set(key, e.target.value)}
      placeholder={placeholder}
      className="w-full bg-[#0B0B0B] border border-[#2A2A2A] text-white font-mono text-sm px-4 py-3 focus:outline-none focus:border-[#CC1A1A] transition-colors"
    />
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Link to="/admin/guides" className="text-[#555] hover:text-white transition-colors">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#CC1A1A] mb-0.5">// EDIT GUIDE</p>
          <h1 className="font-mono text-lg text-white uppercase tracking-tight truncate">{guide.title}</h1>
        </div>
        <a
          href={`/guides/${slug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 font-mono text-[11px] tracking-widest uppercase text-[#9A8F83] hover:text-white transition-colors"
        >
          <ExternalLink className="w-3 h-3" /> VIEW LIVE
        </a>
      </div>

      <div className="space-y-6 max-w-3xl">
        {/* Title */}
        <div>
          {label("Title")}
          {input("title", "Guide title")}
        </div>

        {/* Meta description */}
        <div>
          {label("Meta Description")}
          <textarea
            value={form.meta_description}
            onChange={(e) => set("meta_description", e.target.value)}
            rows={3}
            placeholder="SEO meta description (150–160 chars ideal)"
            className="w-full bg-[#0B0B0B] border border-[#2A2A2A] text-white font-mono text-sm px-4 py-3 focus:outline-none focus:border-[#CC1A1A] transition-colors resize-none"
          />
          <p className="font-mono text-[10px] text-[#555] mt-1">{form.meta_description.length} chars</p>
        </div>

        {/* Hero image */}
        <div>
          {label("Hero Image")}
          <PhotoPicker
            imageUrl={form.hero_image}
            onChange={(v) => set("hero_image", v)}
            onCreditChange={(v) => set("photographer_name", v)}
            credit={form.photographer_name}
            suggestedQuery={form.title || "travel"}
            hidePreview
          />
          <ImagePositioner
            url={form.hero_image}
            position={form.hero_position}
            onChange={(v) => set("hero_position", v)}
          />
          {form.photographer_name && (
            <p style={{ fontFamily: "monospace", fontSize: "10px", color: "#444444", textAlign: "right", marginTop: 4 }}>
              {form.photographer_name}
            </p>
          )}
        </div>

        {/* Content */}
        <div>
          {label("Content")}
          <RichTextEditor
            value={form.content}
            onChange={(v) => set("content", v)}
            testId="guide-content-editor"
          />
        </div>

        {/* Published toggle */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => set("published", !form.published)}
            className={`w-10 h-5 rounded-full transition-colors relative ${form.published ? "bg-[#CC1A1A]" : "bg-[#333]"}`}
          >
            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.published ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
          <span className="font-mono text-[11px] tracking-widest uppercase text-[#9A8F83]">
            {form.published ? "Published (live)" : "Draft (hidden)"}
          </span>
        </div>

        {/* Save */}
        <div className="pt-2">
          <button
            onClick={save}
            disabled={saving}
            className="btn-rd disabled:opacity-50"
          >
            {saving ? "// SAVING…" : "// SAVE GUIDE"}
          </button>
        </div>
      </div>
    </div>
  );
}
